export type WebinarFormValues = {
	firstName: string;
	lastName: string;
	company: string;
	department: string;
	jobTitle: string;
	email: string;
	phone: string;
	companySize: string;
	country: string;
	eventId: string;
	alternateEmail: string;
	marketingOptIn: boolean;
};

export type WebinarFormField = keyof WebinarFormValues;
export type WebinarFormErrors = Partial<Record<WebinarFormField, string>>;

const EMAIL_PATTERN =
	/^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

const PERSONAL_EMAIL_DOMAINS = new Set([
	'aol.com',
	'apple.com',
	'gmail.com',
	'hotmail.com',
	'icloud.com',
	'live.com',
	'mac.com',
	'me.com',
	'msn.com',
	'outlook.com',
	'yahoo.com',
	'ymail.com',
]);

export const isValidWebinarEmail = (email: string): boolean => EMAIL_PATTERN.test(email.trim());

export const getPersonalEmailDomain = (email: string): string | undefined => {
	const domain = email.trim().toLowerCase().split('@')[1];
	return domain && PERSONAL_EMAIL_DOMAINS.has(domain) ? domain : undefined;
};

export type WebinarValidationMessages = {
	required: string;
	invalidEmail: string;
	businessEmail: (domain: string) => string;
};

type WebinarValidationOptions = {
	showPhone: boolean;
	isJapanese: boolean;
	hasMultipleSessions: boolean;
	blockPersonalEmails: boolean;
	messages: WebinarValidationMessages;
};

export const validateWebinarForm = (
	values: WebinarFormValues,
	options: WebinarValidationOptions,
): WebinarFormErrors => {
	const errors: WebinarFormErrors = {};
	const require = (field: WebinarFormField) => {
		const value = values[field];
		if (typeof value === 'string' && !value.trim()) {
			errors[field] = options.messages.required;
		}
	};

	require('firstName');
	require('lastName');
	require('company');
	require('jobTitle');
	require('email');
	require('companySize');
	require('country');

	if (options.showPhone) {
		require('phone');
	}
	if (options.isJapanese) {
		require('department');
	}
	if (options.hasMultipleSessions) {
		require('eventId');
	}

	if (!errors.email && !isValidWebinarEmail(values.email)) {
		errors.email = options.messages.invalidEmail;
	}

	if (!errors.email && options.blockPersonalEmails) {
		const personalDomain = getPersonalEmailDomain(values.email);
		if (personalDomain) {
			errors.email = options.messages.businessEmail(personalDomain);
		}
	}

	return errors;
};
